import { get } from '../components/library/api/rest/index';
import { unwrapXmlStyleContent } from '../utils/apiFieldText';
import { isInactiveSystemStatusRow } from '../utils/systemStatus';
import { buildProcessFlowViewDetailsUrl } from '../utils/processFlowViewerUrl';
import { permitsPathSegment } from './landingDepartmentMerge';
function normalizeRow(row) {
    if (!row || typeof row !== 'object')
        return {};
    const out = {};
    for (const [k, v] of Object.entries(row)) {
        out[k.toLowerCase()] = v;
    }
    return out;
}
function readString(norm, keys) {
    for (const key of keys) {
        const v = norm[key.toLowerCase()];
        if (v == null)
            continue;
        if (typeof v === 'string' && v.trim())
            return unwrapXmlStyleContent(v);
        if (typeof v === 'number' && Number.isFinite(v))
            return String(v);
    }
    return '';
}
/**
 * Only allow absolute URLs that live under the sidebar app (images/icons from sidebar only).
 * Relative paths are joined to `sidebarBase`.
 */
export function resolveSidebarAssetUrl(sidebarBase, raw) {
    const base = sidebarBase.replace(/\/$/, '');
    const t = raw.trim();
    if (!t)
        return '';
    if (/^https?:\/\//i.test(t)) {
        const b = base.toLowerCase();
        if (t.toLowerCase().startsWith(b))
            return t;
        return '';
    }
    const path = t.startsWith('/') ? t : `/${t}`;
    return `${base}${path}`;
}
/**
 * Same-origin full URLs preserved; relative paths joined to sidebar base (see `resolveSidebarAssetUrl`).
 * Handles undefined input gracefully by returning undefined.
 */
function resolveDepartmentTileAssetUrl(sidebarBase, raw) {
    if (!raw || !raw.trim()) {
        return undefined;
    }
    const resolved = resolveSidebarAssetUrl(sidebarBase, raw);
    console.log('[DEBUG] resolveDepartmentTileAssetUrl:', {
        input: raw,
        sidebarBase,
        resolved,
        isAbsolute: /^https?:\/\//i.test(raw),
        isRelative: !raw.startsWith('/') && !/^https?:\/\//i.test(raw),
    });
    return resolved || undefined;
}
/**
 * MUI icon name from the grid row (`ICON` / `icon`). Not used for raster thumbnails.
 * (Raster URLs were removed from grid mapping: large base64 payloads slow the request and often fail in `<img>`.)
 */
function pickIconName(norm) {
    return readString(norm, ['icon', 'muicon']);
}
function mapRowToDepartment(row) {
    const norm = normalizeRow(row);
    /** URL segment `/permits/:id` may match Flow `RecordId`, row `ID`, or a numeric `DEPARTMENT` code column. */
    const recordId = readString(norm, ['recordid', 'id', 'key', 'department']);
    if (!recordId)
        return null;
    let title = readString(norm, ['name', 'title', 'departmentname', 'label', 'departmenttitle', 'displayname']);
    if (!title.trim()) {
        title = readString(norm, ['shortname', 'abbreviation']);
    }
    if (!title.trim())
        return null;
    const subtitle = readString(norm, ['subtitle', 'sub', 'tagline']) || undefined;
    const icon = pickIconName(norm) || undefined;
    const accentHex = readString(norm, ['accentcolor', 'accent', 'themecolor']);
    const accentColor = accentHex.startsWith('#') ? accentHex : undefined;
    const heroImage = readString(norm, ['heroimage', 'backgroundimage', 'hero_image', 'background_image', 'bannerimage']) || undefined;
    const logoImage = readString(norm, ['logoimage', 'logo_image', 'logourl', 'deptlogo', 'departmentlogo']) || undefined;
    return {
        title,
        subtitle,
        recordId,
        icon,
        image: heroImage,
        logoImage,
        imageAlt: title,
        accentColor,
        href: `/permits/${encodeURIComponent(recordId)}`,
    };
}
function gridDataArray(res) {
    const d = res.Data ?? res.data;
    return Array.isArray(d) ? d : [];
}
function gridTotal(res, dataLen) {
    const t = res.Total ?? res.total;
    if (typeof t === 'number' && Number.isFinite(t))
        return t;
    return dataLen;
}
function buildDepartmentsGridFilteredUrl(sidebarBase, gridId, filter, pageSize) {
    const root = sidebarBase.replace(/\/$/, '');
    const params = new URLSearchParams();
    params.set('filter', filter);
    params.set('page', '1');
    params.set('pageSize', String(pageSize));
    return `${root}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(gridId)}?${params.toString()}`;
}
/** Prefer the grid row whose `recordId` / `/permits/...` segment matches the URL `:departmentId`. */
function scoreDepartmentHeroMatch(item, departmentId) {
    const id = departmentId.trim().toLowerCase();
    if (!id)
        return -1;
    const rid = (item.recordId ?? '').trim().toLowerCase();
    if (rid && rid === id)
        return 100;
    const seg = permitsPathSegment(item.href);
    if (seg && seg === id)
        return 95;
    return 10;
}
/**
 * Fetches one row from the departments master grid (`LandingDepartmentsGridId` in env) so `/permits/3`
 * can show a real department title when the agencies grid returns only agency rows.
 * Tries `RecordId`, then `DEPARTMENT` (numeric routes), then `ID` filters.
 */
export async function fetchDepartmentRowForPermitHero(sidebarBase, departmentsGridId, departmentId) {
    const safe = departmentId.replace(/'/g, "''");
    const numeric = /^\d+$/.test(departmentId.trim());
    /** Slug routes (`/permits/commerce`) need `contains` on DEPARTMENT; numeric routes use `eq` to avoid matching e.g. 14 when id is 4. */
    const filters = [];
    if (numeric) {
        filters.push(`RecordId~eq~'${safe}'`);
        filters.push(`DEPARTMENT~eq~'${safe}'`);
        filters.push(`ID~eq~'${safe}'`);
    }
    else {
        filters.push(`DEPARTMENT~contains~'${safe}'`);
        filters.push(`RecordId~eq~'${safe}'`);
        filters.push(`ID~eq~'${safe}'`);
    }
    for (const filter of filters) {
        const url = buildDepartmentsGridFilteredUrl(sidebarBase, departmentsGridId, filter, 25);
        try {
            const json = (await get(url, null, null, null));
            if (!json)
                continue;
            const chunk = gridDataArray(json);
            const candidates = [];
            for (const row of chunk) {
                const item = mapRowToDepartment(row);
                if (item?.title)
                    candidates.push(item);
            }
            if (candidates.length === 0)
                continue;
            let best = candidates[0];
            let bestScore = scoreDepartmentHeroMatch(best, departmentId);
            for (let i = 1; i < candidates.length; i += 1) {
                const item = candidates[i];
                const s = scoreDepartmentHeroMatch(item, departmentId);
                if (s > bestScore) {
                    bestScore = s;
                    best = item;
                }
            }
            return best;
        }
        catch {
            /* try next filter */
        }
    }
    return null;
}
function normalizeHexColorRaw(raw) {
    const t = raw.trim();
    if (!t)
        return undefined;
    const hex = t.startsWith('#') ? t : `#${t}`;
    if (/^#[0-9a-f]{6}$/i.test(hex))
        return hex.toLowerCase();
    if (/^#[0-9a-f]{3}$/i.test(hex)) {
        const x = hex.slice(1);
        return `#${x[0]}${x[0]}${x[1]}${x[1]}${x[2]}${x[2]}`.toLowerCase();
    }
    return undefined;
}
function stripPermitDescriptionHtml(raw) {
    const u = unwrapXmlStyleContent(raw.replace(/<[^>]*>/g, ' '));
    return u.replace(/\s+/g, ' ').trim();
}
function mapRowToLandingPermitCatalogItem(row, processFlowDetailsTemplate) {
    /** `SYSTEM_STATUS = 'I'` (inactive) — never surface the permit in the portal. */
    if (isInactiveSystemStatusRow(row))
        return null;
    const norm = normalizeRow(row);
    const title = readString(norm, ['title', 'name', 'label', 'permitname']);
    const departmentKey = readString(norm, ['department']);
    const recordId = readString(norm, ['recordid']);
    if (!title.trim() || !departmentKey.trim() || !recordId.trim())
        return null;
    const descRaw = readString(norm, ['description', 'desc', 'details', 'summary', 'longdescription']);
    const description = descRaw ? stripPermitDescriptionHtml(descRaw) : undefined;
    const agencyKey = readString(norm, ['agency']) || undefined;
    const applicationCost = readString(norm, ['application_cost', 'applicationcost', 'cost', 'fee']) || undefined;
    const processingTime = readString(norm, ['processing_time', 'processingtime', 'timeframe']) || undefined;
    const scope = readString(norm, ['scope']) || undefined;
    const statewide = readString(norm, ['statewide', 'is_statewide', 'isstatewide']) || undefined;
    const applyNow = readString(norm, ['apply_now', 'applynow', 'apply_now_url', 'applynowurl']) || undefined;
    const flowId = readString(norm, ['flowid']);
    const flowVersion = readString(norm, ['flowversion', 'flow_version']) || '1.0';
    const tpl = processFlowDetailsTemplate?.trim();
    let detailsEmbedUrl;
    if (tpl && flowId.trim()) {
        const u = buildProcessFlowViewDetailsUrl(tpl, flowId.trim(), flowVersion.trim() || '1.0', recordId.trim());
        detailsEmbedUrl = u || undefined;
    }
    return {
        title: title.trim(),
        description: description || undefined,
        departmentKey: departmentKey.trim(),
        agencyKey,
        recordId: recordId.trim(),
        applicationCost,
        processingTime,
        scope,
        statewide,
        applyNow,
        detailsEmbedUrl,
    };
}
function mapRowToAgency(row) {
    const norm = normalizeRow(row);
    const name = readString(norm, ['name']);
    if (!name)
        return null;
    const recordId = readString(norm, ['recordid']);
    const department = readString(norm, ['department']);
    const rowId = readString(norm, ['id']);
    const subtitle = readString(norm, ['subtitle', 'agencytype', 'type', 'division', 'category']) || undefined;
    const description = readString(norm, [
        'description',
        'desc',
        'details',
        'summary',
        'about',
        'longdescription',
        'long_description',
        'agencydescription',
        'descriptiontext',
        'memo',
        'notes',
        'info',
    ]) || undefined;
    const iconFromGrid = pickIconName(norm);
    const colorRaw = readString(norm, ['color', 'accentcolor', 'accent', 'themecolor', 'cardcolor', 'hex']);
    const color = normalizeHexColorRaw(colorRaw) || undefined;
    const agencyGroup = readString(norm, [
        'agencygroup',
        'agency_group',
        'bureau',
        'parentbureau',
        'parentagency',
        'organization',
        'groupname',
        'divisiongroup',
        'agencybureau',
    ]) || undefined;
    const heroImage = readString(norm, ['heroimage', 'backgroundimage', 'hero_image', 'background_image', 'bannerimage']) || undefined;
    const logoImage = readString(norm, ['logoimage', 'logo_image', 'logourl', 'agencylogo']) || undefined;
    return {
        name,
        recordId: recordId || undefined,
        departmentKey: department || undefined,
        rowId: rowId || undefined,
        agencyGroup,
        subtitle,
        description,
        icon: iconFromGrid || undefined,
        color,
        image: heroImage || undefined,
        logoImage: logoImage || undefined,
    };
}
function rankAgencyNameForPriority(name) {
    const n = name.trim().toLowerCase();
    /* `n` is lowercased — compare against lowercase so Business Registration sorts first. */
    if (n.includes('business registration'))
        return 0;
    return 1;
}
function compareAgencyNamesWithPriority(a, b) {
    const ar = rankAgencyNameForPriority(a);
    const br = rankAgencyNameForPriority(b);
    if (ar !== br)
        return ar - br;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
}
/**
 * All agency rows from `{sidebarBase}/api/PublicFlowBuilderRecord/Grid/{gridId}` (paginated).
 */
/**
 * All permit rows from `{sidebarBase}/api/PublicFlowBuilderRecord/Grid/{gridId}?pageSize=&page=` with **no filter**
 * (full catalog for landing search). Paginates until `total` is reached (page size up to 500).
 */
export async function fetchLandingPermitsCatalogFromGrid(sidebarBase, gridId, pageSize = 500, processFlowDetailsTemplate) {
    const root = sidebarBase.replace(/\/$/, '');
    const size = Math.min(500, Math.max(1, Math.floor(pageSize)));
    const all = [];
    const seen = new Set();
    let page = 1;
    let total = Infinity;
    while (all.length < total) {
        const url = `${root}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(gridId)}?pageSize=${size}&page=${page}`;
        const json = (await get(url, null, null, null));
        if (!json) {
            throw new Error('Permits catalog grid failed');
        }
        const chunk = gridDataArray(json);
        total = gridTotal(json, chunk.length);
        for (const row of chunk) {
            const item = mapRowToLandingPermitCatalogItem(row, processFlowDetailsTemplate);
            if (!item)
                continue;
            const key = `${item.departmentKey}\0${item.recordId}`;
            if (seen.has(key))
                continue;
            seen.add(key);
            all.push(item);
        }
        /**
         * Stop when the server returns a partial (or empty) page — that is the last page.
         * Do not page on `all.length < total`: `all.length` is the post-filter/dedup count while
         * `total` is the raw server count, so dropped/duplicate rows would trigger a wasted extra request.
         */
        if (chunk.length < size)
            break;
        page += 1;
        if (page > 500)
            break;
    }
    all.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
    return all;
}
export async function fetchLandingAgenciesFromGrid(sidebarBase, gridId, pageSize = 100) {
    const root = sidebarBase.replace(/\/$/, '');
    const all = [];
    const seen = new Set();
    let page = 1;
    let total = Infinity;
    while (all.length < total) {
        const url = `${root}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(gridId)}?pageSize=${pageSize}&page=${page}`;
        const json = (await get(url, null, null, null));
        if (!json) {
            throw new Error('Agencies grid failed');
        }
        const chunk = gridDataArray(json);
        total = gridTotal(json, chunk.length);
        for (const row of chunk) {
            const item = mapRowToAgency(row);
            if (!item)
                continue;
            const key = item.rowId || item.recordId || item.name;
            if (seen.has(key))
                continue;
            seen.add(key);
            all.push(item);
        }
        /** Last page reached when the server returns fewer rows than requested — see `fetchLandingPermitsCatalogFromGrid`. */
        if (chunk.length < pageSize)
            break;
        page += 1;
        if (page > 500)
            break;
    }
    all.sort((a, b) => compareAgencyNamesWithPriority(a.name, b.name));
    return all;
}
/**
 * Fetches all pages from `{sidebarBase}/api/PublicFlowBuilderRecord/Grid/{gridId}?pageSize=&page=`.
 */
export async function fetchLandingDepartmentsFromGrid(sidebarBase, gridId, pageSize = 100) {
    const root = sidebarBase.replace(/\/$/, '');
    const all = [];
    const seen = new Set();
    let page = 1;
    let total = Infinity;
    while (all.length < total) {
        const url = `${root}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(gridId)}?pageSize=${pageSize}&page=${page}`;
        console.log('[DEBUG] Fetching departments grid:', { url, gridId, page, pageSize });
        const json = (await get(url, null, null, null));
        console.log('[DEBUG] Department grid response:', { ok: !!json, hasData: !!json?.Data || !!json?.data });
        if (!json) {
            console.error('[ERROR] Departments grid returned null for URL:', url);
            throw new Error('Departments grid failed');
        }
        const chunk = gridDataArray(json);
        total = gridTotal(json, chunk.length);
        console.log('[DEBUG] Department grid chunk size:', { chunkLength: chunk.length, total, allLength: all.length });
        for (const row of chunk) {
            const dept = mapRowToDepartment(row);
            console.log('[DEBUG] Mapped department (RAW from API):', {
                title: dept?.title,
                recordId: dept?.recordId,
                image: dept?.image,
                logoImage: dept?.logoImage,
                subtitle: dept?.subtitle,
                icon: dept?.icon,
                hasImage: !!dept?.image,
                hasLogoImage: !!dept?.logoImage,
            });
            if (!dept?.recordId)
                continue;
            if (seen.has(dept.recordId))
                continue;
            seen.add(dept.recordId);
            const resolvedImage = resolveDepartmentTileAssetUrl(sidebarBase, dept.image);
            const resolvedLogo = resolveDepartmentTileAssetUrl(sidebarBase, dept.logoImage);
            const resolvedDept = {
                ...dept,
                image: resolvedImage,
                logoImage: resolvedLogo,
            };
            console.log('[DEBUG] Resolved department (after URL resolution):', {
                title: resolvedDept.title,
                rawImage: dept?.image,
                resolvedImage: resolvedImage,
                rawLogoImage: dept?.logoImage,
                resolvedLogo: resolvedLogo,
                sidebarBase,
            });
            all.push(resolvedDept);
        }
        /** Last page reached when the server returns fewer rows than requested — see `fetchLandingPermitsCatalogFromGrid`. */
        if (chunk.length < pageSize)
            break;
        page += 1;
        if (page > 500)
            break;
    }
    console.log('[DEBUG] Department grid fetch complete:', { count: all.length, total });
    return all;
}
